import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Content,
  Header,
  Page,
  Progress,
  ErrorPanel,
} from '@backstage/core-components';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import {
  CampaignCheck,
  CampaignFilter,
} from '@internal/backstage-plugin-bidone-backend';
import {
  useCampaign,
  useDeleteCampaign,
  useDeleteCampaignCheck,
  useFilter,
  useFilterPreview,
  useFilters,
  useCampaignMembership,
  useFreezeMembership,
  useScheduleCampaign,
  useUnscheduleCampaign,
  useUpdateCampaign,
} from '../../hooks/useCampaigns';
import { StateLabel } from './stateLabel';
import { CampaignProgressMatrix } from './CampaignProgressMatrix';
import { SnapshotCurateDialog } from './SnapshotCurateDialog';
import { CheckEditorDialog } from './CheckEditorDialog';
import { CopyFromRubricDialog } from './CopyFromRubricDialog';
import { EditCampaignDialog } from './EditCampaignDialog';
import { EndCampaignDialog } from './EndCampaignDialog';
import { FilterEditorDialog } from './FilterEditorDialog';

const sectionSx = {
  p: 2,
  mb: 2,
} as const;

const metaLabelSx = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
} as const;

const sectionHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  mb: 1,
} as const;

const sectionActionsSx = {
  display: 'flex',
  gap: 1,
} as const;

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
};

export const CampaignDetailPage = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ?? null;
  const { data: campaign, isFetching, error } = useCampaign(id);
  const { data: filter } = useFilter(campaign?.filterId ?? null);
  const { data: filters } = useFilters();

  const updateCampaign = useUpdateCampaign();
  const deleteCampaign = useDeleteCampaign();
  const freeze = useFreezeMembership();
  const scheduleCampaign = useScheduleCampaign();
  const unscheduleCampaign = useUnscheduleCampaign();
  const deleteCheck = useDeleteCampaignCheck();

  const [editOpen, setEditOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [filterEditorOpen, setFilterEditorOpen] = useState(false);
  const [filterEditorTarget, setFilterEditorTarget] =
    useState<CampaignFilter | null>(null);
  const [checkEditorOpen, setCheckEditorOpen] = useState(false);
  const [checkEditorTarget, setCheckEditorTarget] =
    useState<CampaignCheck | null>(null);
  const [copyRubricOpen, setCopyRubricOpen] = useState(false);
  const [curateOpen, setCurateOpen] = useState(false);
  const [editingBrief, setEditingBrief] = useState(false);
  const [briefDraft, setBriefDraft] = useState('');

  const isSnapshot = campaign?.membershipMode === 'snapshot';
  const { data: membership } = useCampaignMembership(isSnapshot ? id : null);

  const editable = !!campaign && campaign.state !== 'ended';
  const isDraft = campaign?.state === 'draft';

  // Live match count for the attached filter.
  const previewInput = useMemo(
    () =>
      filter
        ? { combine: filter.combine, predicates: filter.predicates }
        : null,
    [filter],
  );
  const { data: filterPreview } = useFilterPreview(previewInput);

  const actionError =
    scheduleCampaign.error ??
    unscheduleCampaign.error ??
    updateCampaign.error ??
    deleteCampaign.error ??
    deleteCheck.error;

  return (
    <Page themeId="tool">
      <Header
        title={campaign?.name || 'Campaign'}
        subtitle={campaign?.ownerEntityRef}
      />
      <Content>
        {error && <ErrorPanel error={error as Error} />}
        {isFetching && !campaign && <Progress />}
        {campaign && (
          <>
            {actionError ? (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {(actionError as Error).message}
              </Alert>
            ) : null}

            {editable && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  marginBottom: 2,
                }}
              >
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setEditOpen(true)}
                >
                  Edit details
                </Button>
                {isDraft && (
                  <>
                    <Button
                      size="small"
                      color="primary"
                      variant="contained"
                      disabled={scheduleCampaign.isPending}
                      onClick={() =>
                        scheduleCampaign.mutate({ id: campaign.id })
                      }
                    >
                      {scheduleCampaign.isPending
                        ? 'Scheduling…'
                        : 'Schedule campaign'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setDeleteOpen(true)}
                      disabled={deleteCampaign.isPending}
                    >
                      Delete draft
                    </Button>
                  </>
                )}
                {!isDraft && (
                  <>
                    <Button
                      size="small"
                      disabled={unscheduleCampaign.isPending}
                      onClick={() =>
                        unscheduleCampaign.mutate({ id: campaign.id })
                      }
                    >
                      {unscheduleCampaign.isPending
                        ? 'Unscheduling…'
                        : 'Unschedule'}
                    </Button>
                    <Button
                      size="small"
                      color="secondary"
                      variant="contained"
                      onClick={() => setEndOpen(true)}
                    >
                      End campaign
                    </Button>
                  </>
                )}
              </Box>
            )}

            <Paper sx={sectionSx}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 2,
                }}
              >
                <div>
                  <Box sx={metaLabelSx}>State</Box>
                  <StateLabel state={campaign.state} />
                </div>
                <div>
                  <Box sx={metaLabelSx}>Owner</Box>
                  <Typography variant="body2">
                    {campaign.ownerEntityRef || '—'}
                  </Typography>
                </div>
                <div>
                  <Box sx={metaLabelSx}>Start</Box>
                  <Typography variant="body2">
                    {formatDate(campaign.startDate)}
                  </Typography>
                </div>
                <div>
                  <Box sx={metaLabelSx}>Target</Box>
                  <Typography variant="body2">
                    {formatDate(campaign.targetDate)}
                  </Typography>
                </div>
                <div>
                  <Box sx={metaLabelSx}>Created</Box>
                  <Typography variant="body2">
                    {formatDate(campaign.createdAt)}
                    {campaign.createdBy && ` by ${campaign.createdBy}`}
                  </Typography>
                </div>
                {campaign.state === 'ended' && (
                  <div>
                    <Box sx={metaLabelSx}>Ended</Box>
                    <Typography variant="body2">
                      {formatDate(campaign.endedAt)}
                      {campaign.endedBy && ` by ${campaign.endedBy}`}
                    </Typography>
                  </div>
                )}
              </Box>
            </Paper>

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Paper sx={sectionSx}>
                  <Box sx={sectionHeaderSx}>
                    <Typography variant="h6">Project brief</Typography>
                    {editable && !editingBrief && (
                      <IconButton
                        size="small"
                        aria-label="Edit brief"
                        onClick={() => {
                          setBriefDraft(campaign.brief);
                          setEditingBrief(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {editingBrief && (
                    <>
                      <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        minRows={6}
                        value={briefDraft}
                        onChange={e => setBriefDraft(e.target.value)}
                        placeholder="What is this campaign about? Why now? What does done look like?"
                      />
                      <Box sx={{ ...sectionActionsSx, mt: 1 }}>
                        <Button
                          size="small"
                          color="primary"
                          variant="contained"
                          disabled={updateCampaign.isPending}
                          onClick={() =>
                            updateCampaign.mutate(
                              {
                                id: campaign.id,
                                patch: { brief: briefDraft },
                              },
                              { onSuccess: () => setEditingBrief(false) },
                            )
                          }
                        >
                          {updateCampaign.isPending ? 'Saving…' : 'Save brief'}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setEditingBrief(false)}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </>
                  )}
                  {!editingBrief && campaign.brief && (
                    <Typography
                      sx={theme => ({
                        whiteSpace: 'pre-wrap',
                        fontFamily: theme.typography.body1.fontFamily,
                      })}
                      component="div"
                    >
                      {campaign.brief}
                    </Typography>
                  )}
                  {!editingBrief && !campaign.brief && (
                    <Typography variant="body2" color="textSecondary">
                      No brief written yet.
                    </Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper sx={sectionSx}>
                  <Box sx={sectionHeaderSx}>
                    <Typography variant="h6">Targeting filter</Typography>
                    {filterPreview && campaign.filterId && (
                      <Chip
                        size="small"
                        color="primary"
                        label={`Matches ${filterPreview.count} of ${filterPreview.total}`}
                      />
                    )}
                  </Box>
                  {editable && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Autocomplete
                        sx={{ flex: 1 }}
                        size="small"
                        options={filters ?? []}
                        getOptionLabel={f => f.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={
                          (filters ?? []).find(
                            f => f.id === campaign.filterId,
                          ) ?? null
                        }
                        onChange={(_e, next) =>
                          updateCampaign.mutate({
                            id: campaign.id,
                            patch: { filterId: next?.id ?? null },
                          })
                        }
                        renderInput={inputParams => (
                          <TextField
                            {...inputParams}
                            label="Saved filter"
                            variant="outlined"
                          />
                        )}
                      />
                      {filter && (
                        <Button
                          size="small"
                          onClick={() => {
                            setFilterEditorTarget(filter);
                            setFilterEditorOpen(true);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setFilterEditorTarget(null);
                          setFilterEditorOpen(true);
                        }}
                      >
                        New
                      </Button>
                    </Box>
                  )}
                  {!campaign.filterId && (
                    <Typography variant="body2" color="textSecondary">
                      No filter — campaign applies to all components.
                    </Typography>
                  )}
                  {campaign.filterId && filter && (
                    <>
                      <Typography variant="subtitle2">
                        {filter.name}{' '}
                        <Typography
                          component="span"
                          variant="caption"
                          color="textSecondary"
                        >
                          (match {filter.combine === 'any' ? 'any' : 'all'})
                        </Typography>
                      </Typography>
                      {filter.predicates.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          Empty filter — matches everything.
                        </Typography>
                      ) : (
                        filter.predicates.map((p, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 0.5,
                            }}
                          >
                            <Box
                              component="code"
                              sx={theme => ({
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                padding: theme.spacing(0.5, 1),
                                background: theme.palette.action.hover,
                                borderRadius: theme.shape.borderRadius,
                              })}
                            >
                              {p.kind === 'entity'
                                ? p.path
                                : `${p.factId}.${p.factKey}`}{' '}
                              {p.op}{' '}
                              {p.value === undefined
                                ? ''
                                : JSON.stringify(p.value)}
                            </Box>
                          </Box>
                        ))
                      )}
                    </>
                  )}
                  {campaign.filterId && !filter && (
                    <Typography variant="body2" color="textSecondary">
                      Filter {campaign.filterId} not found.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Paper sx={sectionSx}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="h6">
                  Checks ({campaign.checks.length})
                </Typography>
                {editable && (
                  <Box sx={sectionActionsSx}>
                    <Button
                      size="small"
                      startIcon={<FileCopyIcon />}
                      onClick={() => setCopyRubricOpen(true)}
                    >
                      Copy from rubric
                    </Button>
                    <Button
                      size="small"
                      color="primary"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setCheckEditorTarget(null);
                        setCheckEditorOpen(true);
                      }}
                    >
                      Add check
                    </Button>
                  </Box>
                )}
              </Box>
              {campaign.checks.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No checks defined yet. A campaign needs at least one check
                  before it can be scheduled.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Origin</TableCell>
                        <TableCell>Facts</TableCell>
                        <TableCell>Description</TableCell>
                        {editable && <TableCell align="right" />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaign.checks.map(c => (
                        <TableRow key={c.id}>
                          <TableCell>{c.name}</TableCell>
                          <TableCell>
                            {c.origin === 'copied-from-rubric'
                              ? 'Copied from rubric'
                              : 'Campaign-defined'}
                          </TableCell>
                          <TableCell>{(c.factIds ?? []).join(', ') || '—'}</TableCell>
                          <TableCell>{c.description || '—'}</TableCell>
                          {editable && (
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                aria-label="Edit check"
                                onClick={() => {
                                  setCheckEditorTarget(c);
                                  setCheckEditorOpen(true);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                aria-label="Delete check"
                                disabled={deleteCheck.isPending}
                                onClick={() =>
                                  deleteCheck.mutate({
                                    campaignId: campaign.id,
                                    checkId: c.id,
                                  })
                                }
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {isSnapshot && (
              <Paper sx={sectionSx}>
                <Box sx={sectionHeaderSx}>
                  <Typography variant="h6">
                    Snapshot membership ({membership?.length ?? 0})
                  </Typography>
                  {editable && (
                    <Button
                      size="small"
                      color="primary"
                      onClick={() => setCurateOpen(true)}
                    >
                      Curate from filter
                    </Button>
                  )}
                </Box>
                <Typography variant="body2" color="textSecondary">
                  Membership is frozen to a curated set — the matrix shows only
                  these components, and new filter matches won't auto-join.
                </Typography>
              </Paper>
            )}

            <Paper sx={sectionSx}>
              <Box sx={sectionHeaderSx}>
                <Typography variant="h6">Progress by component</Typography>
              </Box>
              {campaign.checks.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Add at least one check to see per-component progress.
                </Typography>
              ) : (
                <CampaignProgressMatrix
                  campaignId={campaign.id}
                  editable={editable}
                />
              )}
            </Paper>

            <EditCampaignDialog
              open={editOpen}
              campaign={campaign}
              onClose={() => setEditOpen(false)}
            />
            <EndCampaignDialog
              open={endOpen}
              campaign={campaign}
              onClose={() => setEndOpen(false)}
            />
            <SnapshotCurateDialog
              open={curateOpen}
              campaign={campaign}
              onClose={() => setCurateOpen(false)}
            />
            <FilterEditorDialog
              open={filterEditorOpen}
              filter={filterEditorTarget}
              withSnapshot
              onClose={() => setFilterEditorOpen(false)}
              onSaved={async (saved, snapshotRefs) => {
                const patch: Record<string, unknown> = {};
                if (!filterEditorTarget) patch.filterId = saved.id;
                if (snapshotRefs) patch.membershipMode = 'snapshot';
                if (Object.keys(patch).length) {
                  await updateCampaign.mutateAsync({ id: campaign.id, patch });
                }
                if (snapshotRefs) {
                  await freeze.mutateAsync({
                    campaignId: campaign.id,
                    refs: snapshotRefs,
                  });
                }
              }}
              onDeleted={deletedId => {
                // Detach the filter if the deleted one was attached here.
                if (campaign.filterId === deletedId) {
                  updateCampaign.mutate({
                    id: campaign.id,
                    patch: { filterId: null },
                  });
                }
              }}
            />
            <CheckEditorDialog
              open={checkEditorOpen}
              campaignId={campaign.id}
              check={checkEditorTarget}
              onClose={() => setCheckEditorOpen(false)}
            />
            <CopyFromRubricDialog
              open={copyRubricOpen}
              campaign={campaign}
              onClose={() => setCopyRubricOpen(false)}
            />

            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
              <DialogTitle>Delete draft campaign?</DialogTitle>
              <DialogContent>
                <Typography variant="body2">
                  "{campaign.name}" and its checks will be permanently deleted.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                <Button
                  color="secondary"
                  variant="contained"
                  disabled={deleteCampaign.isPending}
                  onClick={() =>
                    deleteCampaign.mutate(
                      { id: campaign.id },
                      { onSuccess: () => navigate('/campaigns') },
                    )
                  }
                >
                  {deleteCampaign.isPending ? 'Deleting…' : 'Delete'}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Content>
    </Page>
  );
};
